import React, { Suspense, useEffect, useRef, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { logout, selectUser, selectClasses, bootstrapUserShell, ensureUserSchedulingDomains, fetchClassContentBoxes } from "features/userSlice";
import { setDate } from "features/dateSlice";
import { readActiveScenarioSession } from "dev/scenarios/scenarioSession";
import { doc, getDoc, onSnapshot, setDoc, getDocs, collection } from "firebase/firestore";
import { auth, db } from "/Users/michaeltucker/Documents/coding-projects/client/src/firebase.js";
import LandingPage from "views/landing/LandingPage";
import { Button, Spinner, Row } from "reactstrap"
import LogInView from "views/LogInView";
import RegisterView from "views/RegisterView";
import Privacy from "components/Privacy";
import PricingTable from "components/PricingTable";
import ShowRegisterPay from "views/ShowRegisterPay";
import GlobalAudio from "GlobalAudio";
import Logout from "views/pages/Logout";
import LoggedOutPrompt from "views/LoggedOutPrompt";
import SchoolRegister from "views/pages/SchoolRegister";
import SpotifyCallback from "views/callbacks/SpotifyCallback";
import CanvaCallback from "views/callbacks/CanvaCallback";
import CanvaReturn from "views/callbacks/CanvaReturn";
import GoogleDriveCallback from "views/callbacks/GoogleDriveCallback";
import GmailCallback from "views/callbacks/GmailCallback";
import LazyRouteErrorBoundary from "components/LazyRouteErrorBoundary";
import OptionalLazyBoundary from "components/OptionalLazyBoundary";
import RouteSuspenseFallback from "components/RouteSuspenseFallback";
import LinkedActiveDatesChooserModal from "components/ContentBoxes/LinkedActiveDatesChooserModal";
import ReferralRedirect from "views/ReferralRedirect";
import ReferralProgramTerms from "views/ReferralProgramTerms";
import useAdminVideoScrollCapture from "hooks/useAdminVideoScrollCapture";
import useReferralCapture from "hooks/useReferralCapture";
import useStaleModalArtifactCleanup from "hooks/useStaleModalArtifactCleanup";
import { lazyRoute } from "utils/lazyRoute";
import { LazyAdmin, preloadAdmin } from "layouts/lazyAdmin";
import { LazyShare, preloadShare } from "layouts/lazyShare";
import { preloadPresent } from "views/lazyPresent";
import { syncFullstorySession } from "utils/fullstory";
import { syncSentryReplaySession } from "utils/sentryReplay";
import { clearUserScopedStorage } from "utils/clearUserScopedStorage";
import { extractPresentClassIdFromPathname } from "utils/presentClassRouting";
import { CLASSROOM_TOOL_PAGE_PATHS } from "views/classroom-tools/classroomToolPagesContent";
import { syncActiveDatesEducationPreviewFromUrl } from "utils/activeDatesEducationDismiss";
import { hasProductAccess } from "utils/productAccess";
import { isFirestoreInternalAssertionError } from "utils/firestoreInternalAssertion";
import { isFirestorePermissionDeniedError } from "utils/firestoreAuthReady";
import { shouldBlockAppShellOnAuth } from "utils/appShellLoading";
import { getLastAuthUid, hasLikelyAuthSession, rememberLikelyAuthSession } from "utils/likelyAuthSession";
import { ensureUserDocumentForAuthUser, isUserShellProvisioned } from "utils/userProvisioning";
import { reportAppError } from "utils/reportAppError";
import {
  reportAuthBootstrapBlocked,
  reportAuthBootstrapPersistenceReset,
  reportAuthBootstrapRecovered,
  reportAuthBootstrapRetry,
} from "utils/authBootstrapTelemetry";
import {
  clearAuthBootstrapPersistenceResetAttempted,
  resetFirestorePersistenceForAuthBootstrap,
  shouldAttemptAuthBootstrapPersistenceReset,
} from "utils/firestorePersistenceReset";
import { ENABLE_AUTOMATION_PREVIEW, ENABLE_SCREEN_TEMPLATES } from "config/featureFlags";
import { canViewLearningCenter } from "utils/learningCenterAccess";
// Eager CSS so Visitor Rescue cannot take down auth/home via a failed CSS chunk.
import "assets/css/visitor-rescue-survey.css";

const LazyVisitorRescueController = lazyRoute(() => import("components/VisitorRescueSurvey"));
const LazyBlog = lazyRoute(() => import("views/Blog"));
const LazyContact = lazyRoute(() => import("views/Contact"));
const LazyTrustCenter = lazyRoute(() => import("views/TrustCenterPage"));
const LazyDebug = lazyRoute(() => import("views/Debug"));
const LazyAdminViewAsLayout = lazyRoute(() => import("layouts/AdminViewAs"));
const LazyAutomationPreviewLayout = lazyRoute(() => import("layouts/AutomationPreviewLayout"));
const LazyForAdministrators = lazyRoute(() => import("views/ForAdministrators"));
const LazyAmbassadorProgramPage = lazyRoute(() => import("views/ambassadors/AmbassadorProgramPage"));
const LazyPurchaseOrderRequest = lazyRoute(() => import("views/PurchaseOrderRequestPage"));
const LazyPurchaseOrderConfirmation = lazyRoute(() => import("views/PurchaseOrderConfirmationPage"));
const LazyLandingPageTemplate = lazyRoute(() => import("views/LandingPageTemplate"));
const LazyMetronome = lazyRoute(() => import("views/Metronome"));
const LazyMusicTools = lazyRoute(() => import("views/MusicTools"));
const LazyMusicToolsEmbed = lazyRoute(() => import("views/MusicToolsEmbed"));
const LazyOutOfTune = lazyRoute(() => import("views/OutOfTune"));
const LazyToneGenerator = lazyRoute(() => import("views/ToneGenerator"));
const LazyTuner = lazyRoute(() => import("views/Tuner"));
const LazyMetronomePage = lazyRoute(() => import("views/tools/MetronomePage"));
const LazyToneGeneratorPage = lazyRoute(() => import("views/tools/ToneGeneratorPage"));
const LazyTunerPage = lazyRoute(() => import("views/tools/TunerPage"));
const LazyToolsHub = lazyRoute(() => import("views/ToolsHubPage"));
const LazyClassroomTimerPage = lazyRoute(() => import("views/tools/ClassroomTimerPage"));
const LazyCountdownTimerPage = lazyRoute(() => import("views/tools/CountdownTimerPage"));
const LazyRandomStudentPickerPage = lazyRoute(() => import("views/tools/RandomStudentPickerPage"));
const LazyClassroomScreenAlternative = lazyRoute(() => import("views/ClassroomScreenAlternative"));
const LazyComparisonPage = lazyRoute(() => import("views/ComparisonPage"));
const LazyClassroomAutomationPage = lazyRoute(() => import("views/classroom-automation/ClassroomAutomationPage"));
const LazyClassroomToolPage = lazyRoute(() => import("views/classroom-tools/ClassroomToolPage"));
const LazyContestPage = lazyRoute(() => import("views/ContestPage"));
const LazyDemoScenarioPage = lazyRoute(() => import("views/DemoScenarioPage"));
const LazySubscriptionPage = lazyRoute(() => import("views/SubscriptionPage"));
const LazyStripeCheckoutHelpPage = lazyRoute(() => import("components/StripeCheckoutHelpPage"));
const LazyWhatsNewPage = lazyRoute(() => import("views/WhatsNewPage"));
const LazyBackToSchoolPreviewPage = lazyRoute(() => import("views/BackToSchoolPreviewPage"));
const LazyWelcomeBackPreviewPage = lazyRoute(() => import("views/WelcomeBackPreviewPage"));
const LazyLearningCenterPage = lazyRoute(() => import("views/learning/LearningCenterPage"));
const LazyLearningSessionPage = lazyRoute(() => import("views/learning/LearningSessionPage"));
const LazyLearningJoinPage = lazyRoute(() => import("views/learning/LearningJoinPage"));
const LazyMobileSignupHandoffPage = lazyRoute(() => import("views/MobileSignupHandoffPage"));
const LazyContinueSetupPage = lazyRoute(() => import("views/ContinueSetupPage"));
const LazyScreenTemplatesPage = lazyRoute(() => import("views/ScreenTemplatesPage"));
const LazyActiveDatesVsLinkedBoxesPage = lazyRoute(() => import("views/ActiveDatesVsLinkedBoxesPage"));
const LazyScenarioDevLogin = process.env.NODE_ENV !== "production"
  ? lazyRoute(() => import("dev/scenarios/ScenarioDevLogin"))
  : null;
const AUTH_BOOTSTRAP_SESSION_KEY = "authBootstrapPending";
const USER_BOOTSTRAP_RETRY_DELAYS_MS = [0, 1200, 3000];

const getSentryEnvironment = () => process.env.REACT_APP_ENV || process.env.NODE_ENV || "unknown";

const resolveSentryAccountType = (user) => {
  if (user?.premium === true) return "paid";
  if (user?.trial === true) return "trial";
  if (user?.premium === false && user?.trial === false) return "inactive";
  return "unknown";
};

const resolveSentryUserRole = (user) => {
  if (user?.settings?.role === "admin") return "admin";
  if (user?.uid) return "teacher";
  return "unknown";
};

const wait = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const resolveActiveClassIdFromPathname = (pathname, classes) => {
  if (!classes?.length) {
    return null;
  }

  const validClassIds = new Set(classes.map((cls) => cls.classId).filter(Boolean));

  const managerMatch = pathname.match(/\/(?:content-manager|calendar)\/([^/]+)/);
  if (managerMatch?.[1] && validClassIds.has(managerMatch[1])) {
    return managerMatch[1];
  }

  const pathClassId = extractPresentClassIdFromPathname(pathname, validClassIds);
  if (pathClassId) {
    return pathClassId;
  }

  return classes[0]?.classId || null;
};

function App() {
    const location = useLocation();
    const user = useSelector(selectUser);
    const classes = useSelector(selectClasses);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true)
    const [firebaseInitialized, setFirebaseInitialized] = useState(false);
    const [sharedUser, setSharedUser] = useState(null);
    const [authUser, setAuthUser] = useState(undefined);
    const [bootstrapError, setBootstrapError] = useState(null);
    const [bootstrapRetryCount, setBootstrapRetryCount] = useState(0);
    const classBoxUnsubsRef = useRef(new Map());
    const bootstrapRunIdRef = useRef(0);
    const bootstrapStartedAtRef = useRef(null);
    const bootstrapFailureSeenRef = useRef(false);
    const lastBootstrapFailureCategoryRef = useRef("unknown");
    const lastReportedBootstrapFailureRef = useRef("");
    const schedulingPrefetchRunIdRef = useRef(0);
    const prevAuthUidRef = useRef(null);
    const adminVideoScrollCaptureModal = useAdminVideoScrollCapture(user);
    useReferralCapture({ skipWhenAuthenticated: Boolean(authUser) });
    useStaleModalArtifactCleanup();

    useEffect(() => {
      syncActiveDatesEducationPreviewFromUrl();
    }, []);

    useEffect(() => {
      if (process.env.NODE_ENV === "production") return;
      if (!user || isLoading) return;
      if (process.env.REACT_APP_USE_FIREBASE_EMULATORS !== "true") return;

      const session = readActiveScenarioSession();
      if (!session?.activeDateKey) return;

      const [year, month, day] = session.activeDateKey.split("-").map(Number);
      dispatch(setDate({ year, month: month - 1, day }));
    }, [dispatch, isLoading, user]);

    useEffect(() => {
      const unsubscribe = auth.onAuthStateChanged((userAuth) => {
        setAuthUser(userAuth || null);
        setBootstrapError(null);

        if (userAuth) {
          if (
            prevAuthUidRef.current !== userAuth.uid
            || !Number.isFinite(bootstrapStartedAtRef.current)
          ) {
            bootstrapStartedAtRef.current = Date.now();
            bootstrapFailureSeenRef.current = false;
            lastBootstrapFailureCategoryRef.current = "unknown";
            lastReportedBootstrapFailureRef.current = "";
          }
          preloadAdmin();
          prevAuthUidRef.current = userAuth.uid;
          rememberLikelyAuthSession(userAuth.uid);
          sessionStorage.setItem(AUTH_BOOTSTRAP_SESSION_KEY, "true");
          setIsLoading(true);
          return;
        }

        const uidToClear = prevAuthUidRef.current || getLastAuthUid();
        clearUserScopedStorage(uidToClear);
        prevAuthUidRef.current = null;
        bootstrapStartedAtRef.current = null;
        bootstrapFailureSeenRef.current = false;
        lastBootstrapFailureCategoryRef.current = "unknown";
        lastReportedBootstrapFailureRef.current = "";
        sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
        dispatch(logout());
        setIsLoading(false);
      });
    
      return () => unsubscribe();
    }, [dispatch]);

    useEffect(() => {
      if (!authUser?.uid) {
        return undefined;
      }

      let isCancelled = false;
      const runId = ++bootstrapRunIdRef.current;

      const bootstrapAuthenticatedUser = async () => {
        setIsLoading(true);
        setBootstrapError(null);

        let lastError = null;

        for (const delayMs of USER_BOOTSTRAP_RETRY_DELAYS_MS) {
          if (delayMs > 0) {
            await wait(delayMs);
          }

          if (isCancelled || bootstrapRunIdRef.current !== runId) {
            return;
          }

          try {
            const shellResult = await dispatch(
              bootstrapUserShell(authUser.uid, {
                forceAuthRefresh: bootstrapRetryCount > 0,
              })
            );

            if (isCancelled || bootstrapRunIdRef.current !== runId) {
              return;
            }

            if (!isUserShellProvisioned(shellResult?.userData)) {
              await ensureUserDocumentForAuthUser(authUser);

              if (isCancelled || bootstrapRunIdRef.current !== runId) {
                return;
              }

              await dispatch(bootstrapUserShell(authUser.uid));
            }

            if (isCancelled || bootstrapRunIdRef.current !== runId) {
              return;
            }

            sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
            clearAuthBootstrapPersistenceResetAttempted();
            setBootstrapError(null);
            setIsLoading(false);

            const prefetchRunId = ++schedulingPrefetchRunIdRef.current;
            void dispatch(ensureUserSchedulingDomains(authUser.uid)).finally(() => {
              if (schedulingPrefetchRunIdRef.current !== prefetchRunId) {
                return;
              }
            });
            return;
          } catch (error) {
            lastError = error;
            console.error("Error loading user data:", error);
          }
        }

        if (isCancelled || bootstrapRunIdRef.current !== runId) {
          return;
        }

        if (shouldAttemptAuthBootstrapPersistenceReset(lastError)) {
          const diagnostics = reportAuthBootstrapPersistenceReset({
            error: lastError,
            uid: authUser.uid,
            route: location.pathname,
            retryCount: bootstrapRetryCount,
            automaticAttemptCount: USER_BOOTSTRAP_RETRY_DELAYS_MS.length,
            startedAt: bootstrapStartedAtRef.current,
            alreadyReset: false,
          });
          lastBootstrapFailureCategoryRef.current = diagnostics.failureCategory;

          const resetResult = await resetFirestorePersistenceForAuthBootstrap(db);
          if (isCancelled || bootstrapRunIdRef.current !== runId) {
            return;
          }
          if (resetResult.reloadStarted) {
            return;
          }

          reportAppError(
            resetResult.reloadError || new Error("Auth bootstrap persistence reset did not reload."),
            {
              feature: "authentication",
              action: "auth_bootstrap_persistence_reset_failed",
              uid: authUser.uid,
              route: location.pathname,
              failureCategory: diagnostics.failureCategory,
            }
          );
        }

        const missingUserDoc = /User document not found/i.test(String(lastError?.message || ""));
        const incompleteUserShell = /Account setup is still finishing/i.test(String(lastError?.message || ""));
        if (missingUserDoc || incompleteUserShell) {
          try {
            await ensureUserDocumentForAuthUser(authUser);
            if (isCancelled || bootstrapRunIdRef.current !== runId) {
              return;
            }
            await dispatch(bootstrapUserShell(authUser.uid));
            if (isCancelled || bootstrapRunIdRef.current !== runId) {
              return;
            }
            sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
            clearAuthBootstrapPersistenceResetAttempted();
            setBootstrapError(null);
            setIsLoading(false);
            void dispatch(ensureUserSchedulingDomains(authUser.uid));
            return;
          } catch (healError) {
            lastError = healError;
            reportAppError(healError, {
              feature: "user-provisioning",
              action: "app-bootstrap-heal",
              uid: authUser.uid,
              dedupeKey: `app-bootstrap-heal:${authUser.uid}`,
            });
          }
        }

        setBootstrapError(lastError || new Error("Unable to load account data."));
        setIsLoading(false);
      };

      bootstrapAuthenticatedUser();

      return () => {
        isCancelled = true;
        schedulingPrefetchRunIdRef.current += 1;
      };
    }, [authUser?.uid, bootstrapRetryCount, dispatch]);

    useEffect(() => {
      if (!authUser?.uid || !bootstrapError || user) {
        return;
      }

      const failureKey = [
        authUser.uid,
        bootstrapRetryCount,
        bootstrapError?.code || "unknown",
        bootstrapError?.message || "unknown",
      ].join(":");
      if (lastReportedBootstrapFailureRef.current === failureKey) {
        return;
      }

      lastReportedBootstrapFailureRef.current = failureKey;
      bootstrapFailureSeenRef.current = true;
      const diagnostics = reportAuthBootstrapBlocked({
        error: bootstrapError,
        uid: authUser.uid,
        route: location.pathname,
        retryCount: bootstrapRetryCount,
        automaticAttemptCount: USER_BOOTSTRAP_RETRY_DELAYS_MS.length,
        startedAt: bootstrapStartedAtRef.current,
      });
      lastBootstrapFailureCategoryRef.current = diagnostics.failureCategory;
    }, [
      authUser?.uid,
      bootstrapError,
      bootstrapRetryCount,
      location.pathname,
      user,
    ]);

    useEffect(() => {
      if (
        !authUser?.uid
        || !user?.uid
        || isLoading
        || !bootstrapFailureSeenRef.current
      ) {
        return;
      }

      reportAuthBootstrapRecovered({
        uid: authUser.uid,
        route: location.pathname,
        retryCount: bootstrapRetryCount,
        startedAt: bootstrapStartedAtRef.current,
        failureCategory: lastBootstrapFailureCategoryRef.current,
      });
      bootstrapFailureSeenRef.current = false;
    }, [
      authUser?.uid,
      bootstrapRetryCount,
      isLoading,
      location.pathname,
      user?.uid,
    ]);

    useEffect(() => {
      if (user && !isLoading) {
        preloadAdmin();
        preloadPresent();
      }
    }, [user, isLoading]);

    useEffect(() => {
      syncFullstorySession({
        authUser,
        user,
        pathname: location.pathname,
      });
      syncSentryReplaySession({
        pathname: location.pathname,
      });
    }, [authUser, user, location.pathname]);

    useEffect(() => {
      if (!authUser?.uid || !user?.uid || authUser.uid !== user.uid) {
        Sentry.setUser(null);
        Sentry.setTag("account_type", "unknown");
        Sentry.setTag("app_user_role", "unknown");
        Sentry.setTag("environment", getSentryEnvironment());
        return;
      }

      Sentry.setUser({ id: user.uid });
      Sentry.setTag("account_type", resolveSentryAccountType(user));
      Sentry.setTag("app_user_role", resolveSentryUserRole(user));
      Sentry.setTag("environment", getSentryEnvironment());
      Sentry.setContext("lesson_launchpad_account", {
        hasClasses: classes.length > 0,
        classCount: classes.length,
      });
    }, [authUser?.uid, user, classes.length]);

    useEffect(() => {
      if (location.pathname.startsWith("/share")) {
        preloadShare();
        preloadPresent();
      }
    }, [location.pathname]);

    useEffect(() => {
      const handleUserGesture = () => {
        console.log("👋 User gesture detected!");
    
        if (!sessionStorage.getItem("audioUnlocked")) {
          console.log("🔓 Attempting to unlock audio...");
          window.unlockAudioPlayback?.();
        }
    
        // Remove listeners after first trigger
        window.removeEventListener("click", handleUserGesture);
        window.removeEventListener("keydown", handleUserGesture);
        window.removeEventListener("touchstart", handleUserGesture);
      };
    
      if (!sessionStorage.getItem("audioUnlocked")) {
        console.log("🎧 Waiting for user gesture to unlock audio...");
    
        window.addEventListener("click", handleUserGesture);
        window.addEventListener("keydown", handleUserGesture);
        window.addEventListener("touchstart", handleUserGesture);
      }
    
      return () => {
        window.removeEventListener("click", handleUserGesture);
        window.removeEventListener("keydown", handleUserGesture);
        window.removeEventListener("touchstart", handleUserGesture);
      };
    }, []);    

      /*
       * Why this listener-map exists:
       * `fetchClassContentBoxes` creates a live Firestore `onSnapshot` subscription and returns
       * an unsubscribe function. A naive `classes.forEach(dispatch(fetchClassContentBoxes(...)))`
       * inside an effect can re-run on unrelated renders and create duplicate listeners for the
       * same class. Duplicate listeners cause repeated callbacks, stale updates, and hard-to-debug
       * state races.
       *
       * This block keeps exactly one active listener per subscribed class id by diffing the
       * wanted subscription set:
       * - remove listeners for classes that are no longer active or deleted
       * - add a full-collection listener only for the current Content Manager class route
       *
       * Redux `selectedClassContentBoxes` is retained when a listener is removed so revisiting
       * a class can show cached data until the next snapshot arrives.
       *
       * The cleanup effect below then guarantees all listeners are unsubscribed on user change/
       * unmount so no background listeners leak into the next session.
       */
      useEffect(() => {
        if (!user?.uid || location.pathname.includes("/share")) return;

        const managerMatch = location.pathname.match(/\/content-manager\/([^/]+)/);
        const validClassIds = new Set(
          classes.map((cls) => cls.classId).filter(Boolean)
        );
        const managerClassId =
          managerMatch?.[1] && validClassIds.has(managerMatch[1]) ? managerMatch[1] : null;
        const wantedIds = new Set(managerClassId ? [managerClassId] : []);
        const listenerMap = classBoxUnsubsRef.current;

        Array.from(listenerMap.keys()).forEach((classId) => {
          if (!wantedIds.has(classId) || !validClassIds.has(classId)) {
            try {
              listenerMap.get(classId)?.();
            } catch (error) {
              // no-op
            }
            listenerMap.delete(classId);
          }
        });

        wantedIds.forEach((classId) => {
          if (!listenerMap.has(classId)) {
            const unsubscribe = dispatch(fetchClassContentBoxes(user.uid, classId));
            if (typeof unsubscribe === "function") {
              listenerMap.set(classId, unsubscribe);
            }
          }
        });

      }, [dispatch, user?.uid, classes, location.pathname]);

      useEffect(() => {
        if (!user?.uid) return undefined;

        return () => {
          const listenerMap = classBoxUnsubsRef.current;
          listenerMap.forEach((unsubscribe) => {
            try {
              unsubscribe();
            } catch (error) {
              // no-op
            }
          });
          listenerMap.clear();
        };
      }, [user?.uid]);
      
      
      // const getUserIDforShareCode = async (shareCode) => {
      //   try {
      //     const docRef = doc(db, "shareCodes", shareCode);
      //     const docSnap = await getDoc(docRef);
    
      //     if (docSnap.exists()) {
      //       const shareCodeDataFetch = docSnap.data();
      //       // console.log("shareCodeDataFetch => ", shareCodeDataFetch);
      //       return shareCodeDataFetch.uid; // Assuming userID is part of the document data
      //     } else {
      //       console.log("No such document!");
      //     }
      //   } catch (error) {
      //     console.error('Error fetching document:', error);
      //   }
      // };

      // useEffect(() => {
      //   const fetchSharedClasses = async () => {
      //     if (!sharedUser?.uid) return;
      //     const classesRef = collection(db, `usersNew/${sharedUser.uid}/classes`);
      //     const snapshot = await getDocs(classesRef);
      //     const classes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      //     setSharedUser(prev => ({ ...prev, classes }));
      //   };
      
      //   if (sharedUser) {
      //     fetchSharedClasses();
      //   }
      // }, [sharedUser]);      

      //must also uncomment script in index.html to start using this again.
      //only runs mailchimp script on landingpage.
    //   useEffect(() => {
    //     // Dynamically load Mailchimp script if on the LandingPage component
    //     if (location.pathname === "/" && !user) {
    //         const script = document.createElement("script"); 
    //         script.id = "mcjs";
    //         script.async = true;
    //         script.src = "https://chimpstatic.com/mcjs-connected/js/users/d6aaa500bb03c2821bd199093/1005a6f4accfc551bf159b59e.js";
    //         document.head.appendChild(script);
    //     }
    // }, [location.pathname]);

      const isOAuthCallbackRoute =
        location.pathname === "/spotify-callback"
        || location.pathname === "/canva-callback"
        || location.pathname === "/canva-return"
        || location.pathname === "/google-drive-callback"
        || location.pathname === "/gmail-callback";
      const shouldShowLoadingShell = shouldBlockAppShellOnAuth({
        pathname: location.pathname,
        isLoading,
        authUser,
        isOAuthCallbackRoute,
        hasLikelyAuthSession: hasLikelyAuthSession(),
      });

      const handleRetryBootstrap = () => {
        const nextRetryCount = bootstrapRetryCount + 1;
        reportAuthBootstrapRetry({
          uid: authUser?.uid,
          route: location.pathname,
          retryCount: nextRetryCount,
          failureCategory: lastBootstrapFailureCategoryRef.current,
        });
        setBootstrapRetryCount(nextRetryCount);
      };

      const handleReloadAfterBootstrapFailure = () => {
        window.location.reload();
      };

      const handleSignOutAfterBootstrapFailure = async () => {
        sessionStorage.removeItem(AUTH_BOOTSTRAP_SESSION_KEY);
        setBootstrapError(null);
        setIsLoading(true);

        try {
          await auth.signOut();
        } catch (error) {
          console.error("Error signing out after bootstrap failure:", error);
          setIsLoading(false);
        }
      };

      if (shouldShowLoadingShell) {
        return (
          <>
            <GlobalAudio />
            <Row
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh'
              }}
            >
              <Spinner style={{ height: '3rem', width: '3rem' }} color="info">Loading...</Spinner>
            </Row>
          </>
        );
      }

      if (authUser && bootstrapError && !user && !isOAuthCallbackRoute) {
        const needsReload = isFirestoreInternalAssertionError(bootstrapError);
        const isPermissionDenied = isFirestorePermissionDeniedError(bootstrapError);
        return (
          <>
            <GlobalAudio />
            <div
              data-sentry-block
              style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px",
                backgroundColor: "white"
              }}
            >
              <div
                style={{
                  maxWidth: "540px",
                  width: "100%",
                  textAlign: "center",
                  padding: "32px 28px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "18px",
                  boxShadow: "0 10px 35px rgba(15, 23, 42, 0.08)"
                }}
              >
                <h2 style={{ color: "#004aad", fontWeight: "bold", marginBottom: "12px" }}>
                  We signed you in, but could not load your account.
                </h2>
                <p style={{ marginBottom: "12px", color: "#374151" }}>
                  {needsReload
                    ? "Your browser's offline cache for Lesson Launchpad got stuck. Reloading the page usually fixes this."
                    : isPermissionDenied
                      ? "Your sign-in finished, but Lesson Launchpad could not finish authorizing account access yet. Try Again usually fixes this."
                    : "This usually means the connection to Firestore was interrupted while your account data was loading."}
                </p>
                {authUser?.email ? (
                  <p style={{ marginBottom: "12px", color: "#4b5563" }}>
                    Signed in as <strong>{authUser.email}</strong>
                  </p>
                ) : null}
                {!needsReload ? (
                  <p style={{ marginBottom: "24px", color: "#6b7280" }}>
                    {isPermissionDenied
                      ? "If Try Again does not work, sign out, clear site data for lessonlaunchpad.com, and sign in again."
                      : (bootstrapError?.message || "Please check your internet connection and try again.")}
                  </p>
                ) : (
                  <div style={{ marginBottom: "24px" }} />
                )}
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                  {needsReload ? (
                    <Button
                      color="info"
                      className="button-hover-fix"
                      style={{ borderRadius: "999px", minWidth: "160px" }}
                      onClick={handleReloadAfterBootstrapFailure}
                    >
                      Reload Page
                    </Button>
                  ) : (
                    <Button
                      color="info"
                      className="button-hover-fix"
                      style={{ borderRadius: "999px", minWidth: "160px" }}
                      onClick={handleRetryBootstrap}
                    >
                      Try Again
                    </Button>
                  )}
                  <Button
                    color="secondary"
                    outline
                    style={{ borderRadius: "999px", minWidth: "160px" }}
                    onClick={handleSignOutAfterBootstrapFailure}
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </div>
          </>
        );
      }

      return (
        <>
          <GlobalAudio />
          <LinkedActiveDatesChooserModal />
          {adminVideoScrollCaptureModal}
          {!user ? (
            <OptionalLazyBoundary feature="visitor_rescue">
              <Suspense fallback={null}>
                <LazyVisitorRescueController />
              </Suspense>
            </OptionalLazyBoundary>
          ) : null}
          <LazyRouteErrorBoundary>
            <Suspense fallback={<RouteSuspenseFallback />}>
              <Switch>
            {/* Spotify OAuth callback — must be reachable before auth/loading state resolves */}
            <Route exact path="/spotify-callback" component={SpotifyCallback} />
            <Route exact path="/canva-callback" component={CanvaCallback} />
            <Route exact path="/canva-return" component={CanvaReturn} />
            <Route exact path="/google-drive-callback" component={GoogleDriveCallback} />
            <Route exact path="/gmail-callback" component={GmailCallback} />

            {/* Auth and public routes */}
            <Route exact path="/auth/register" component={RegisterView} />
            <Route exact path="/continue-setup" component={LazyContinueSetupPage} />
            <Route exact path="/r/:referralCode" component={ReferralRedirect} />
            <Route exact path="/referral-program-terms" component={ReferralProgramTerms} />
            <Route exact path="/auth/login" component={LogInView} />
            <Route exact path="/auth/logout" component={Logout} />
            <Route exact path="/privacy" component={Privacy} />
            <Route exact path="/trust" component={LazyTrustCenter} />
            <Route exact path="/contact" component={LazyContact} />
            <Route exact path="/why-lesson-launchpad" component={LazyLandingPageTemplate} />
            <Route exact path="/active-dates-vs-linked-boxes" component={LazyActiveDatesVsLinkedBoxesPage} />
            {/* Register outside Admin for signed-out users and logged-in users without product access (reactivation gate CTA). Active users keep the Admin-shell route from routes.js. */}
            {(!user || !hasProductAccess(user)) ? (
              <Route exact path="/whats-new" component={LazyWhatsNewPage} />
            ) : null}
            <Route exact path="/back-to-school-preview" component={LazyBackToSchoolPreviewPage} />
            <Route exact path="/welcome-back" component={LazyWelcomeBackPreviewPage} />
            <Route exact path="/live" render={() => <Redirect to="/learn" />} />
            {/* Public /learn for signed-out users and logged-in users without product access.
                Active users get the Admin-shell routes from routes.js (with Sidebar).
                Join stays full-bleed for everyone. */}
            {(!user || !hasProductAccess(user)) ? (
              <Route
                exact
                path="/learn"
                render={() => (canViewLearningCenter(user) ? <LazyLearningCenterPage /> : <LoggedOutPrompt />)}
              />
            ) : null}
            {(!user || !hasProductAccess(user)) ? (
              <Route
                exact
                path="/learn/session/:occurrenceId"
                render={() => (canViewLearningCenter(user) ? <LazyLearningSessionPage /> : <LoggedOutPrompt />)}
              />
            ) : null}
            <Route
              exact
              path="/learn/join/:occurrenceId"
              render={() => (canViewLearningCenter(user) ? <LazyLearningJoinPage /> : <LoggedOutPrompt />)}
            />
            <Route exact path="/show" component={ShowRegisterPay} />
            <Route exact path="/show-pricing" component={PricingTable} />
            <Route exact path="/pricing/checkout-help" component={LazyStripeCheckoutHelpPage} />
            <Route path="/pricing" component={PricingTable} />
            <Route
              exact
              path="/account/subscription"
              render={() => (
                user
                  ? <LazySubscriptionPage />
                  : <LoggedOutPrompt />
              )}
            />
            <Route exact path="/blog" component={LazyBlog} />
            <Route path="/blog/:blogId" component={LazyBlog} />
            <Route path="/demo/:scenario" component={LazyDemoScenarioPage} />
            <Route exact path="/embed/metronome" component={LazyMetronome} />
            <Route exact path="/embed/tone-generator" component={LazyToneGenerator} />
            <Route exact path="/embed/tuner" component={LazyTuner} />
            <Route exact path="/embed/music-tools" component={LazyMusicToolsEmbed} />
            <Route exact path="/metronome" component={LazyMetronomePage} />
            <Route exact path="/tone-generator" component={LazyToneGeneratorPage} />
            <Route exact path="/out-of-tune" component={LazyOutOfTune} />
            <Route exact path="/music-tools" component={LazyMusicTools} />
            <Route exact path="/tuner" component={LazyTunerPage} />
            <Route exact path="/tools" component={LazyToolsHub} />
            <Route exact path="/classroom-timer" component={LazyClassroomTimerPage} />
            <Route exact path="/countdown-timer" component={LazyCountdownTimerPage} />
            <Route exact path="/random-student-picker" component={LazyRandomStudentPickerPage} />
            <Route exact path="/what-is-classroom-automation" component={LazyClassroomAutomationPage} />
            <Route exact path={CLASSROOM_TOOL_PAGE_PATHS} component={LazyClassroomToolPage} />
            <Route exact path="/classroomscreen-alternative" component={LazyClassroomScreenAlternative} />
            <Route exact path="/compare/classroomscreen-vs-lesson-launchpad" component={LazyComparisonPage} />
            <Route exact path="/compare/google-slides-classroom-timer" component={LazyComparisonPage} />
            <Route exact path="/compare/smartboard-classroom-tools" component={LazyComparisonPage} />
            <Route exact path="/debug" component={LazyDebug} />
            <Route exact path="/school/:schoolId" component={SchoolRegister} />
            <Route
              exact
              path="/mobile-signup-handoff"
              render={() => (user ? <LazyMobileSignupHandoffPage /> : <LoggedOutPrompt />)}
            />
            {ENABLE_SCREEN_TEMPLATES ? (
              <Route
                exact
                path="/screen-templates"
                render={() => (user ? <LazyScreenTemplatesPage /> : <LoggedOutPrompt />)}
              />
            ) : null}
            <Route exact path="/for-administrators" component={LazyForAdministrators} />
            <Route exact path="/ambassadors" component={LazyAmbassadorProgramPage} />
            <Route exact path="/ambassador-program" render={() => <Redirect to="/ambassadors" />} />
            <Route exact path="/purchase-order" component={LazyPurchaseOrderRequest} />
            <Route exact path="/quote" render={() => <Redirect to="/purchase-order" />} />
            <Route exact path="/purchase-order/confirmation" component={LazyPurchaseOrderConfirmation} />
            <Route exact path="/purchase-order/confirmation/:requestId" render={() => <Redirect to="/purchase-order/confirmation" />} />
            <Route exact path="/purchase-order/upload/:requestId" render={() => <Redirect to="/purchase-order/confirmation" />} />
            <Route
              exact
              path="/contest"
              render={() => {
                if (!user) return <LoggedOutPrompt />;
                if (user?.settings?.role !== "admin") return <Redirect to="/" />;
                return <LazyContestPage />;
              }}
            />
            {LazyScenarioDevLogin ? (
              <Route path="/dev/scenario-login/:scenarioId" component={LazyScenarioDevLogin} />
            ) : null}
            {/* Share Layout should only apply to /share routes */}
            <Route path="/share" component={LazyShare} />
            {user ? <Route path="/admin/view-as/:uid" component={LazyAdminViewAsLayout} /> : null}
            {user && ENABLE_AUTOMATION_PREVIEW ? (
              <Route path="/automation-preview" component={LazyAutomationPreviewLayout} />
            ) : null}

            {/* Ensure user routes go through AdminLayout */}
            {user ? (
              <Route path="/" component={LazyAdmin} />
            ) : (
              [
                <Route key="landing-home" exact path="/" component={LandingPage} />,
                <Route key="logged-out-fallback" path="/" component={LoggedOutPrompt} />,
              ]
            )}
              </Switch>
            </Suspense>
          </LazyRouteErrorBoundary>
        </>
      );
}

export default App;